import { createReducer, isAnyOf } from '@reduxjs/toolkit'
import cloneDeep from 'lodash/cloneDeep'
import flow from 'lodash/fp/flow'
import set from 'lodash/fp/set'
import get from 'lodash/get'

import {
    acceptInvitationFailure,
    acceptInvitationRequest,
    acceptInvitationSuccess,
    acceptSignUpFormInvitationFailure,
    acceptSignUpFormInvitationRequest,
    acceptSignUpFormInvitationSuccess,
    changeOrganizationFailure,
    changeOrganizationGroupFailure,
    changeOrganizationGroupRequest,
    changeOrganizationRequest,
    createAuthPageOrganizationFailure,
    createAuthPageOrganizationRequest,
    createAuthPageOrganizationSuccess,
    fetchLatestOrganizationAdIntegrationsSuccess,
    fetchLatestOrganizationIntegrationSuccess,
    fetchPendingInvitationsSuccess,
    fetchProfileFeaturePermissionsSuccess,
    fetchUserFeaturePermissionsSuccess,
    fetchUserOrganizationFeaturePermissionsSuccess,
    fetchUserSettingsSuccess,
    getSignUpFormInvitationSuccess,
    impersonationModeFailure,
    loadAuthFailure,
    loadAuthFinish,
    loadAuthRequest,
    resendCodeRequest,
    resendCodeSuccess,
    resendSignUpCodeSuccess,
    resetAuthErrors,
    resetPasswordFailure,
    resetPasswordRequest,
    sendResetEmailFailure,
    sendResetEmailRequest,
    sendResetEmailSuccess,
    setOrganizationId,
    setUserIsImpersonating,
    signInFailure,
    signInMfaRequest,
    signInMfaRequired,
    signInRequest,
    signInSuccess,
    signOutFailure,
    signOutRequest,
    signOutSuccess,
    signUpFailure,
    signUpRequest,
    signUpSuccess,
    verifyEmailFailure,
    verifyEmailRequest,
    verifyEmailSuccess,
} from 'actions/auth'
import { AuthState } from 'types'

const defaultState: AuthState = {
    signedIn: false,
    username: null,
    organizationId: null,
    organizationGroupId: null,
    userFeaturePermissions: [],
    featurePermissions: [],
    profileFeaturePermissions: {},
    latestOrganizationIntegration: null,
    latestOrganizationAdIntegrations: null,
    email: null,
    email_verified: null,
    name: null,
    phone_number: null,
    phone_number_verified: null,
    displayMaintenancePage: false,
    displayDowntimePage: false,
    isLoadingAuth: true, // defaults to true so loading indicator is always shown by default when application loads
    isFetching: false,
    loadAuthError: null,
    error: null,
    password: null,
    invitation: null,
    ipAddress: null,
    date_joined: null,
    id: null,
    pendingInvitations: [],
    userIsImpersonating: false, // persists to local storage
    settings: null,
    isFetchingCode: false,
}

export default createReducer(cloneDeep(defaultState), (builder) => {
    builder
        .addCase(loadAuthRequest, (state) => ({
            ...state,
            isLoadingAuth: true,
            loadAuthError: null,
        }))
        .addCase(loadAuthFinish, (state, action) => {
            const {
                displayMaintenancePage = false,
                displayDowntimePage = false,
            } = action.payload || {}
            return {
                ...state,
                isLoadingAuth: false,
                displayMaintenancePage,
                displayDowntimePage,
            }
        })
        .addCase(loadAuthFailure, (state, action) => {
            const { error } = action.payload
            return {
                ...state,
                isLoadingAuth: false,
                loadAuthError: error,
            }
        })
        .addCase(signOutSuccess, () => ({
            ...cloneDeep(defaultState),
            isLoadingAuth: false,
        }))
        .addCase(resetAuthErrors, (state) => ({
            ...state,
            error: null,
        }))
        .addCase(signInSuccess, (state, action) => {
            const {
                currentUser: {
                    username,
                    date_joined,
                    id,
                    email,
                    first_name,
                    last_name,
                    phone_number,
                },
                organizationId,
                organizationGroupId,
            } = action.payload

            return {
                ...state,
                signedIn: true,
                isFetching: false,
                error: null,
                password: null,

                username,
                date_joined,
                id,
                organizationId: organizationId ?? null,
                organizationGroupId: organizationGroupId ?? null,
                email,
                email_verified: true,
                name: `${first_name} ${last_name}`,
                phone_number: phone_number || null,
                phone_number_verified: false,
            }
        })
        .addCase(sendResetEmailRequest, (state, action) => {
            const { email } = action.payload
            return {
                ...state,
                isFetching: true,
                error: null,
                email,
            }
        })
        .addCase(signInMfaRequired, (state) => ({
            ...state,
            isFetching: false,
        }))
        .addCase(resendCodeRequest, (state) => ({
            ...state,
            isFetching: false,
            isFetchingCode: true,
        }))
        .addCase(resendCodeSuccess, (state) => ({
            ...state,
            isFetching: false,
            isFetchingCode: false,
        }))
        .addCase(fetchUserFeaturePermissionsSuccess, (state, action) => {
            const userFeaturePermissions = action.payload
            return {
                ...state,
                userFeaturePermissions,
            }
        })
        .addCase(
            fetchUserOrganizationFeaturePermissionsSuccess,
            (state, action) => {
                const featurePermissions = action.payload
                return {
                    ...state,
                    featurePermissions,
                }
            }
        )
        .addCase(fetchUserSettingsSuccess, (state, action) => {
            const settings = action.payload
            return {
                ...state,
                settings: {
                    ...(state.settings ?? {}),
                    ...settings,
                },
            }
        })
        .addCase(changeOrganizationGroupRequest, (state, action) => {
            const { organizationId, organizationGroupId } = action.payload
            return {
                ...state,
                organizationId,
                organizationGroupId,
            }
        })
        .addCase(fetchLatestOrganizationIntegrationSuccess, (state, action) => {
            const latestIntegrationCreatedDate = get(
                action,
                ['payload', 'results', '0', 'created_at'],
                null
            )
            return set(
                ['latestOrganizationIntegration'],
                latestIntegrationCreatedDate,
                state
            )
        })
        .addCase(
            fetchLatestOrganizationAdIntegrationsSuccess,
            (state, action) => {
                const latestAdIntegrations = get(
                    action,
                    ['payload', 'results'],
                    null
                )
                return set(
                    ['latestOrganizationAdIntegrations'],
                    latestAdIntegrations,
                    state
                )
            }
        )
        .addCase(getSignUpFormInvitationSuccess, (state, action) =>
            flow(
                set(['invitation'], action.payload),
                set(['isFetching'], false),
                set(['error'], null)
            )(state)
        )
        .addCase(acceptSignUpFormInvitationSuccess, (state) =>
            flow(
                set(['invitation'], null),
                set(['isFetching'], false),
                set(['error'], null)
            )(state)
        )
        .addCase(fetchProfileFeaturePermissionsSuccess, (state, action) => {
            const { profileId, permissions } = action.payload
            return set(
                ['profileFeaturePermissions', profileId],
                permissions,
                state
            )
        })
        .addCase(fetchPendingInvitationsSuccess, (state, action) =>
            set(['pendingInvitations'], action.payload.results)(state)
        )
        .addCase(setUserIsImpersonating, (state, action) => {
            const isImpersonating = action.payload
            return flow(
                set(['userIsImpersonating'], isImpersonating),
                set(['isLoadingAuth'], true)
            )(state)
        })
        .addMatcher(
            isAnyOf(
                signUpRequest,
                signInRequest,
                signInMfaRequest,
                signOutRequest,
                resetPasswordRequest,
                createAuthPageOrganizationRequest,
                verifyEmailRequest,
                acceptSignUpFormInvitationRequest,
                acceptInvitationRequest
            ),
            (state) => ({
                ...state,
                isFetching: true,
                error: null,
            })
        )
        .addMatcher(
            isAnyOf(
                createAuthPageOrganizationSuccess,
                verifyEmailSuccess,
                sendResetEmailSuccess,
                acceptInvitationSuccess
            ),
            (state) => ({
                ...state,
                isFetching: false,
                error: null,
            })
        )
        .addMatcher(
            isAnyOf(signUpSuccess, resendSignUpCodeSuccess),
            (state, action) => {
                const { email, password } = action.payload
                return {
                    ...state,
                    isFetching: false,
                    error: null,
                    email,
                    password,
                }
            }
        )
        .addMatcher(
            isAnyOf(
                signUpFailure,
                signOutFailure,
                signInFailure,
                sendResetEmailFailure,
                resetPasswordFailure,
                createAuthPageOrganizationFailure,
                verifyEmailFailure,
                acceptSignUpFormInvitationFailure,
                acceptInvitationFailure,
                impersonationModeFailure
            ),
            (state, action) => {
                const { error } = action.payload
                return {
                    ...state,
                    isFetching: false,
                    error,
                }
            }
        )
        .addMatcher(
            isAnyOf(changeOrganizationRequest, setOrganizationId),
            (state, action) => {
                const { organizationId } = action.payload
                return {
                    ...state,
                    organizationId,
                }
            }
        )
        .addMatcher(
            isAnyOf(changeOrganizationFailure, changeOrganizationGroupFailure),
            (state, action) => {
                const { error } = action.payload
                return {
                    ...state,
                    error,
                }
            }
        )
})
